<template>
	<div class="formviewer2">

		<div class="row" v-for="field in schema.components" :key="field.id">
			<div v-if="field.type === 'textfield'" class="col-12 mb-3">
				<label class="control-label visible-ie8 visible-ie9" :for="field.key">{{ field.label }}</label>
				<input :class="{'form-control': true, 'is-invalid': errors.has(field.key)}"
				       v-validate="'required'"
				       data-vv-delay="500"
				       :data-vv-as="field.label"
				       :data-vv-name="field.key"
				       type="text"
				       :placeholder="field.label"
				       :name="field.key"
				       :id="field.key"
				       @input="setInput($event.target.value, field.key)"
				       v-model="submission[field.key]"
				       :disabled="readOnly"
				/>
				<div class="invalid-tooltip d-block" v-if="errors.has(field.key)">{{ errors.first(field.key) }}</div>
			</div>

			<div class="col-12 mb-3" v-else-if="field.type === 'textarea'">
				<label class="control-label visible-ie8 visible-ie9" :for="field.key">{{ field.label }}</label>
				<textarea :class="{'form-control': true, 'is-invalid': errors.has(field.key)}"
				          v-validate="'required'"
				          data-vv-delay="500"
				          :data-vv-as="field.label"
				          :data-vv-name="field.key"
				          type="text"
				          :placeholder="field.label"
				          :name="field.key"
				          :id="field.key"
				          @input="setInput($event.target.value, field.key)"
				          v-model="submission[field.key]"
				          v-if="!readOnly"
				>
        </textarea>
				<div v-if="readOnly" v-html="submission[field.key]" class="border p-3 bg-gray-200">
				</div>
				<div class="invalid-tooltip d-block" v-if="errors.has(field.key)">{{ errors.first(field.key) }}</div>
			</div>

			<div class="col-12 mb-3" v-else-if="field.type === 'content'" v-html="field.html"></div>


			<div class="col-12 mb-3" v-else-if="field.type === 'checkbox'">
				<b-form-checkbox
						:id="field.key"
						v-model="submission[field.key]"
						@input="setInput($event.target.value, field.key)"
						:name="field.key"
						:value="true"
						:unchecked-value="false"
						:disabled="readOnly"
				>
					{{ field.label }}
				</b-form-checkbox>
			</div>

			<div class="col-12 mb-3" v-else-if="field.type === 'selectboxes'">
				<b-form-group :label="field.label">
					<b-form-checkbox-group v-model="submission[field.key]">
						<template v-for="(value, index) in field.values">
							<b-form-checkbox
									:value="value.value"
									:key="index + value.label"
									:disabled="readOnly"
									@input="setSelectBoxesInput($event, field.key, value.label)">{{ value.value }}
							</b-form-checkbox>
						</template>
					</b-form-checkbox-group>
				</b-form-group>
			</div>

			<div class="col-12 pb-3 pt-3 mb-3" v-else-if="field.type === 'radio'">
				{{ field.label }}
				<b-form-radio-group :label="field.label" @change="setInput($event.target.value, field.key)"
				                    :disabled="readOnly">
					<b-form-radio v-model="submission[field.key]" :name="field.label" :value="radio.value"
					              v-for="radio in field.values" :key="radio.value" :disabled="readOnly">{{ radio.label
						}}
					</b-form-radio>
				</b-form-radio-group>
			</div>

			<div class="col-12 mb-3" v-else-if="field.type === 'button'">
				<loading-button :loading="loading" @submit="submitHandler" block v-if="!readOnly"
				                :disabled="errors.any()">Submit
				</loading-button>
			</div>

			<div class="col-12 mb-3" v-else-if="field.type === 'signature'">
				<label>{{ field.label }}</label>
				<vue-signature-pad width="100%"
				                   height="150px"
				                   :ref="field.key"
				                   :options="{ backgroundColor: '#f2f2f2', onEnd: () => { saveSignature(field) } }"
				                   v-if="!readOnly"
				>
				</vue-signature-pad>
				<img :src="submission[field.key]" v-if="readOnly" class="img-fluid">
			</div>

			<div class="col-12 mb-3" v-else-if="field.type === 'file'">
				<label>{{ field.label }}</label>

				<div class="mb-3" v-if="!readOnly">
					<vue-dropzone :ref="field.key" :id="field.key" :options="dropzoneOptions"
					              @vdropzone-success="mediaUploaded($event, field)"></vue-dropzone>
				</div>

				<table class="table table-bordered" v-if="submission[field.key]">
					<thead>
					<tr>
						<th>File</th>
						<th>Size</th>
					</tr>
					</thead>
					<tbody>
					<tr v-for="file in submission[field.key]" :key="file.id">
						<td>
							<a href="#" @click.prevent="showFile(file)" target="_blank">
								{{ file.name }}
							</a>
						</td>
						<td>
							{{ file.size }}
						</td>
					</tr>
					</tbody>
				</table>

			</div>

			<div v-else>
				{{ field.type }}
				<div class="alert alert-danger">If you see this message, please contact support letting us know,
					including which assignment you're working on
				</div>
			</div>


		</div>
	</div>
</template>

<script>
	import vue2Dropzone from 'vue2-dropzone'
	import {debounce} from 'debounce'

	export default {
		name: 'FormViewer',
		components: {
			vueDropzone: vue2Dropzone
		},
		data() {
			return {
				loading: false,
				submission: {},
				signatureOptions: {backgroundColor: '#f2f2f2', onEnd: this.saveSignature}
			}
		},
		methods: {
			setInput(e, field) {
				this.submission[field] = e
				this.$emit('input', this.submission)
				this.syncFormState()
			},
			setSelectBoxesInput(e, field, key) {
				this.submission[field][key] = e
				this.$emit('input', this.submission)
				this.syncFormState()
			},
			submitHandler() {
				this.$validator.validateAll().then((res) => {
					if (res) {
						this.loading = true
						return this.onSubmit(this.submission).then(() => {
							this.loading = false
						}).catch(() => {
							this.loading = false
						})
					}
				})
			},
			syncFormState: debounce(function () {
				this.axios.post('/submissions/' + this.formId + '/sync', {data: this.submission})
			}, 1000),
			saveSignature(field) {
				const {data} = this.$refs[field.key][0].saveSignature()
				this.submission[field.key] = data
			},
			mediaUploaded(e, field) {
				let image = JSON.parse(e.xhr.response)
				if (!Array.isArray(this.submission[field.key])) {
					this.submission[field.key] = []
				}
				this.submission[field.key].push(image)
				this.$emit('input', this.submission)
				//this.$refs[field.key][0].removeFile(e)
			},
			showFile(file) {
				if (/(jpg|jpeg|png|gif|bmp)/.test(file.url)) {
					this.$modal.show({
						template: `
							<div>
								<img :src="url" class="img-fluid">
							</div>
						`,
						props: ['url']
					}, {
						url: file.url
					}, {
						height: 'auto',
						//width: 'auto',
						adaptive: true
					}, {})
				} else {
					this.$modal.show({
						template: `
							<div>
								<embed :src="url" height="100%" class="embed-responsive"
								       style="min-height: 500px;min-width: 500px;"/>
								<a :href="url" target="_blank" class="btn btn-block btn-primary">Open</a>
							</div>
						`,
						props: ['url']
					}, {
						url: file.url
					}, {
						height: 'auto',
						//width: 'auto',
						adaptive: true
					}, {})
				}
			}
		},
		beforeMount() {
			this.submission = this.value
		},
		computed: {
			dropzoneOptions() {
				return {
					url: this.$config.API_URL + '/forms/' + this.formId + '/upload',
					//thumbnailWidth: 150,
					maxFilesize: 20000,
					//acceptedFiles: '*',
					headers: {'Authorization': 'Bearer ' + this.$auth.token()},
					dictDefaultMessage: `
          <div class="text-center text-black-50">
            <i class="icon-cloud-upload" style="font-size: 5em"></i>
            <h4 class="mt-2 mb-1">Drag & Drop a File</h4>
            <h6 class="ui-font-Montserrat-Regular">Or select an option below</h6>
            <div class="drag_add">
              <span class="btn btn-primary">Add File</span>
            </div>
          </div>
        `
				}
			}
		},
		props: {
			schema: {
				type: Object,
				required: true
			},
			value: {
				type: Object
			},
			formId: {
				required: true
			},
			onSubmit: {},
			readOnly: {
				type: Boolean,
				default: false
			}
		}
	}
</script>

<style lang="scss">
	@import "~vue2-dropzone/dist/vue2Dropzone.min.css";

	.formviewer2 {
	}
</style>
